import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatTarih, formatTl } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import Ipucu from '../../ui/Ipucu';
import { CHANNEL_LABEL, MOVE_REASON_LABEL, UNIT_LABEL, type Unit } from '@artmozaik/shared';
import Ikon from './ikon';
import AciklamaDuzenleyici, { ACIKLAMA_SINIR, AciklamaGoster } from './Aciklama';
import { Belgeler, MedyaIzgara, useDosyaIslem } from './Medya';
import MuhasebeBagi from './Muhasebe';
import StokDuzelt from './StokDuzelt';
import {
  BIRIMLER, KATEGORILER, KATEGORI_AD, OZELLIKLER, OZELLIK_ETIKET, alanMetni, donusOku, kaydet as dosyaKaydet, miktar, ozellikAnahtari,
  sayiOku, siraOku, type Detay, type Gecmis,
} from './ortak';

/**
 * ÜRÜN SAYFASI — Shopify "Ürün" düzeni. Hem ürün kartı (`/stok/:id`) hem yeni
 * ürün (`/stok/yeni`) bu bileşeni kullanır.
 *
 * Sol sütun kartlar (ürün bilgisi, medya, fiyat, stok, teknik özellikler,
 * geçmiş); sağ sütun hafif bölümler (durum, satışlar, organizasyon, site,
 * muhasebe programı, not). Alanlar doğrudan düzenlenir; bir alan değişince
 * tepede "Kaydedilmemiş değişiklikler" çubuğu belirir ve yalnız DEĞİŞEN alanlar
 * tek istekte gönderilir. Görseller ve stok düzeltme anında işlenir.
 *
 * Yetki: kart alanları `product.write`; eşik ve not `stock.write` ile de
 * değişir; maliyet alanları yalnız sunucunun gönderdiği rolde çizilir.
 */
type Form = {
  code: string; name: string; description: string;
  listPriceTl: string; vatPct: string; maxDiscountPct: string; costTl: string; purchaseDiscountPct: string;
  threshold: string; unit: string; boxM2: string;
  category: string; brand: string; siteName: string; notes: string; active: boolean;
  specs: { ad: string; deger: string }[];
};
type Hatalar = Partial<Record<keyof Form, string>>;

const BOS: Form = {
  code: '', name: '', description: '', listPriceTl: '', vatPct: '20', maxDiscountPct: '', costTl: '', purchaseDiscountPct: '',
  threshold: '15', unit: 'KUTU', boxM2: '', category: 'URETIM', brand: '', siteName: '', notes: '', active: true, specs: [],
};

function formdan(d: Detay): Form {
  const bilinen = OZELLIKLER.filter(([k]) => d.specs[k]).map(([k, etiket]) => ({ ad: etiket, deger: d.specs[k] }));
  const ek = Object.entries(d.specs).filter(([k, v]) => !OZELLIK_ETIKET.has(k) && v).map(([ad, deger]) => ({ ad, deger }));
  return {
    code: d.code, name: d.name, description: d.description ?? '',
    listPriceTl: d.listPriceKurus == null ? '' : alanMetni(d.listPriceKurus / 100),
    vatPct: alanMetni(d.vatPct), maxDiscountPct: alanMetni(d.maxDiscountPct),
    costTl: d.costKurus == null ? '' : alanMetni(d.costKurus / 100), purchaseDiscountPct: alanMetni(d.purchaseDiscountPct),
    threshold: alanMetni(d.threshold), unit: d.unit, boxM2: alanMetni(d.boxM2),
    category: d.category, brand: d.brand ?? '', siteName: d.siteName ?? '', notes: d.notes ?? '', active: d.active,
    specs: [...bilinen, ...ek],
  };
}

function specsNesnesi(satirlar: Form['specs']): Record<string, string> {
  const o: Record<string, string> = {};
  for (const s of satirlar) { const k = ozellikAnahtari(s.ad); if (k && s.deger.trim()) o[k] = s.deger.trim(); }
  return o;
}

function dogrula(f: Form, yeni: boolean, maliyetGorur: boolean): Hatalar {
  const h: Hatalar = {};
  const aralik = (k: keyof Form, ad: string, { bosOlur = true, min = 0, max = Infinity, sifirdanBuyuk = false } = {}) => {
    const v = sayiOku(f[k] as string);
    if (v === null) { if (!bosOlur) h[k] = `${ad} boş olamaz`; return; }
    if (Number.isNaN(v)) h[k] = `${ad} sayı olmalı`;
    else if (sifirdanBuyuk && v <= 0) h[k] = `${ad} 0'dan büyük olmalı`;
    else if (v < min || v > max) h[k] = max === Infinity ? `${ad} ${min} ya da daha büyük olmalı` : `${ad} ${min}–${max} arasında olmalı`;
  };
  if (yeni && f.code.trim().length < 2) h.code = 'Kod en az 2 karakter olmalı';
  if (f.name.trim().length < 2) h.name = 'Ürün adı en az 2 harf olmalı';
  else if (f.name.trim().length > 200) h.name = 'Ürün adı en çok 200 karakter olabilir';
  if (f.description.length > ACIKLAMA_SINIR) h.description = `Açıklama en çok ${ACIKLAMA_SINIR} karakter olabilir`;
  aralik('listPriceTl', 'Liste fiyatı');
  aralik('vatPct', 'KDV', { bosOlur: false, max: 100 });
  aralik('maxDiscountPct', 'İskonto tavanı', { max: 100 });
  aralik('threshold', 'Eşik', { bosOlur: false });
  aralik('boxM2', 'Kutu m²', { sifirdanBuyuk: true });
  if (maliyetGorur) { aralik('costTl', 'Birim maliyet', { max: 20_000_000 }); aralik('purchaseDiscountPct', 'Alış iskontosu', { max: 100 }); }
  if (f.brand.trim().length > 80) h.brand = 'Marka en çok 80 karakter olabilir';
  if (f.siteName.trim().length > 200) h.siteName = 'Sitedeki ad en çok 200 karakter olabilir';
  if (f.notes.trim().length > 500) h.notes = 'Not en çok 500 karakter olabilir';
  const gorulen = new Set<string>();
  for (const s of f.specs) {
    const ad = s.ad.trim(); const deger = s.deger.trim();
    if (!ad && !deger) continue;
    const k = ozellikAnahtari(ad);
    if (!ad) h.specs = 'Özelliğin adı boş olamaz';
    else if (k.length > 40) h.specs = `"${ad}": özellik adı en çok 40 karakter olabilir`;
    else if (deger.length > 200) h.specs = `"${ad}": değer en çok 200 karakter olabilir`;
    else if (gorulen.has(k)) h.specs = `"${ad}" iki kez girilmiş`;
    gorulen.add(k);
  }
  if (gorulen.size > 40) h.specs = 'En çok 40 teknik özellik girilebilir';
  return h;
}

/** İlk hâle göre DEĞİŞEN alanların PATCH gövdesi. */
function degisenler(f: Form, ilk: Form, maliyetGorur: boolean): Record<string, unknown> {
  const g: Record<string, unknown> = {};
  const metin = (k: 'brand' | 'siteName' | 'notes' | 'description', ad = k as string) => { if (f[k].trim() !== ilk[k].trim()) g[ad] = f[k].trim() || null; };
  const sayi = (k: keyof Form, ad: string, bos: number | null = null) => { if (f[k] !== ilk[k]) g[ad] = sayiOku(f[k] as string) ?? bos; };
  if (f.name.trim() !== ilk.name.trim()) g.name = f.name.trim();
  metin('description'); metin('brand'); metin('siteName'); metin('notes');
  if (f.category !== ilk.category) g.category = f.category;
  if (f.unit !== ilk.unit) g.unit = f.unit;
  if (f.active !== ilk.active) g.active = f.active;
  sayi('listPriceTl', 'listPriceTl'); sayi('maxDiscountPct', 'maxDiscountPct'); sayi('vatPct', 'vatPct', 20);
  sayi('boxM2', 'boxM2'); sayi('threshold', 'threshold', 0);
  if (maliyetGorur) { sayi('costTl', 'costTl'); sayi('purchaseDiscountPct', 'purchaseDiscountPct'); }
  if (JSON.stringify(specsNesnesi(f.specs)) !== JSON.stringify(specsNesnesi(ilk.specs))) g.specs = specsNesnesi(f.specs);
  return g;
}

const TEKLIF_DURUM: Record<string, string> = {
  TASLAK: 'taslak', GONDERILDI: 'gönderildi', ONAY_BEKLIYOR: 'onay bekliyor', ONAYLANDI: 'onaylandı', REDDEDILDI: 'reddedildi', IPTAL: 'iptal',
};

export default function UrunSayfasi({ id }: { id: number | null }) {
  const yeni = id == null;
  const { can } = useAuth();
  const git = useNavigate();
  const [sorgu] = useSearchParams();
  const kopyaId = yeni ? Number(sorgu.get('kopya')) || null : null;

  const [d, setD] = useState<Detay | null>(null);
  const [gecmis, setGecmis] = useState<Gecmis | null>(null);
  const [markalar, setMarkalar] = useState<string[]>([]);
  const [yukHata, setYukHata] = useState<string | null>(null);
  const [ilk, setIlk] = useState<Form | null>(yeni && !kopyaId ? BOS : null);
  const [f, setF] = useState<Form | null>(yeni && !kopyaId ? BOS : null);
  const [hatalar, setHatalar] = useState<Hatalar>({});
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [menu, setMenu] = useState(false);
  const [stokKutu, setStokKutu] = useState(false);
  const [tumHareket, setTumHareket] = useState(false);
  const [kodAcik, setKodAcik] = useState(false);
  const [yeniKod, setYeniKod] = useState('');
  const [kodNot, setKodNot] = useState('');
  const bildir = useMemo(() => ({ hata: setHata, bilgi: setBilgi }), []);

  const urunYazar = can('product.write');
  const stokYazar = can('stock.write');
  const esikYazar = urunYazar || stokYazar;
  const maliyetGorur = yeni ? can('quote.cost') : d?.maliyet !== undefined;
  const maliyetYazar = maliyetGorur && esikYazar;

  /** Sunucudan oku. `formuKoru`: görsel / stok gibi anlık işlemlerden sonra yazılmakta olan alanlar silinmesin. */
  const yukle = useCallback(async (formuKoru = false) => {
    if (id == null) return;
    try {
      const x = await api<Detay>(`/api/urunler/${id}`);
      setD(x); setYukHata(null);
      const taze = formdan(x);
      setIlk(taze);
      // Form korunurken yalnız kod eşitlenir (kod ayrı uçtan değişir; "kaydedilmemiş" sayılmasın).
      setF((eski) => (formuKoru && eski ? { ...eski, code: taze.code } : taze));
    } catch (e) { setYukHata((e as Error).message); }
  }, [id]);
  const yenile = useCallback(() => yukle(true), [yukle]);

  useEffect(() => {
    setD(null); setGecmis(null); setHata(null); setBilgi(null); setHatalar({}); setTumHareket(false); setKodAcik(false); setStokKutu(false);
    if (id != null) {
      setIlk(null); setF(null);
      void yukle();
      api<Gecmis>(`/api/urunler/${id}/gecmis`).then(setGecmis).catch(() => undefined);
    }
  }, [id, yukle]);

  // Yeni ürün "kopyala" ile açıldıysa kaynak ürünün alanları dolu gelir (kod ve görseller hariç).
  useEffect(() => {
    if (!kopyaId) return;
    api<Detay>(`/api/urunler/${kopyaId}`)
      .then((x) => { const k = { ...formdan(x), code: '', name: `${x.name} (kopya)`, active: true }; setIlk(BOS); setF(k); })
      .catch((e) => { setIlk(BOS); setF(BOS); setHata(`Kopyalanacak ürün okunamadı: ${(e as Error).message}`); });
  }, [kopyaId]);

  // Marka önerileri (mevcut markalar).
  useEffect(() => {
    api<{ ozet: { markalar?: string[] } }>('/api/urunler?pasifDahil=1&limit=1').then((r) => setMarkalar(r.ozet.markalar ?? [])).catch(() => undefined);
  }, []);

  const kirli = !!f && !!ilk && JSON.stringify(f) !== JSON.stringify(ilk);
  const kirliRef = useRef(false);
  kirliRef.current = kirli && !kaydediliyor;

  /* Kaydedilmemiş değişiklik varken sayfadan çıkış: sekme kapanışı + panel içi bağlantılar. */
  useEffect(() => {
    const kapanis = (e: BeforeUnloadEvent) => { if (kirliRef.current) { e.preventDefault(); e.returnValue = ''; } };
    const tikla = (e: MouseEvent) => {
      if (!kirliRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (a.pathname === window.location.pathname && a.search === window.location.search) return;
      if (!window.confirm('Kaydedilmemiş değişiklikler var. Sayfadan çıkılsın mı?')) { e.preventDefault(); e.stopPropagation(); }
      else kirliRef.current = false;
    };
    window.addEventListener('beforeunload', kapanis);
    document.addEventListener('click', tikla, true);
    return () => { window.removeEventListener('beforeunload', kapanis); document.removeEventListener('click', tikla, true); };
  }, []);
  const guvenliGit = (yol: string) => {
    if (kirliRef.current && !window.confirm('Kaydedilmemiş değişiklikler var. Sayfadan çıkılsın mı?')) return;
    kirliRef.current = false;
    git(yol);
  };

  useEffect(() => {
    if (!menu) return;
    const kapat = () => setMenu(false);
    const t = window.setTimeout(() => document.addEventListener('click', kapat), 0);
    return () => { window.clearTimeout(t); document.removeEventListener('click', kapat); };
  }, [menu]);

  const al = <K extends keyof Form>(k: K, v: Form[K]) => {
    setF((x) => (x ? { ...x, [k]: v } : x));
    setHatalar((h) => (h[k] ? { ...h, [k]: undefined } : h));
  };

  async function kaydet() {
    if (!f || !ilk) return;
    const h = dogrula(f, yeni, maliyetGorur);
    setHatalar(h);
    const ilkHata = Object.values(h).find(Boolean);
    if (ilkHata) { setHata(ilkHata); return; }
    setHata(null); setBilgi(null); setKaydediliyor(true);
    try {
      if (yeni) {
        const r = await api<{ id: number }>('/api/urunler', { method: 'POST', body: JSON.stringify({
          code: f.code.trim().toUpperCase(), name: f.name.trim(), category: f.category, unit: f.unit,
          boxM2: sayiOku(f.boxM2) ?? undefined, threshold: sayiOku(f.threshold) ?? 0,
          listPriceTl: sayiOku(f.listPriceTl) ?? undefined, maxDiscountPct: sayiOku(f.maxDiscountPct) ?? undefined,
          vatPct: sayiOku(f.vatPct) ?? 20, notes: f.notes.trim() || undefined,
        }) });
        // Oluşturma ucunun almadığı alanlar (marka, açıklama, özellikler, site adı, maliyet, pasif) ikinci adımda yazılır.
        const temel: Form = { ...BOS, code: f.code, name: f.name, category: f.category, unit: f.unit, boxM2: f.boxM2, threshold: f.threshold,
          listPriceTl: f.listPriceTl, maxDiscountPct: f.maxDiscountPct, vatPct: f.vatPct, notes: f.notes };
        const ek = degisenler(f, temel, maliyetGorur);
        if (Object.keys(ek).length) {
          try { await api(`/api/urunler/${r.id}`, { method: 'PATCH', body: JSON.stringify(ek) }); }
          catch (e) { window.alert(`Ürün açıldı ama bazı alanlar yazılamadı: ${(e as Error).message}\nÜrün kartında yeniden deneyin.`); }
        }
        kirliRef.current = false;
        git(`/stok/${r.id}`, { replace: true });
        return;
      }
      const govde = degisenler(f, ilk, maliyetGorur);
      if (Object.keys(govde).length === 0) { setF(ilk); return; }
      await api(`/api/urunler/${id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      await yukle();
      setBilgi('Ürün kaydedildi');
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Ürün kaydedilemedi');
    } finally { setKaydediliyor(false); }
  }

  // ⌘S / Ctrl+S kaydeder.
  const kaydetRef = useRef(kaydet);
  kaydetRef.current = kaydet;
  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (kirliRef.current) void kaydetRef.current(); }
    };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, []);

  async function kodDegistir(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBilgi(null);
    try {
      const r = await api<{ eskiKod: string; yeniKod: string }>(`/api/urunler/${id}/kod`, { method: 'POST', body: JSON.stringify({ code: yeniKod, note: kodNot }) });
      setBilgi(`Kod ${r.eskiKod} → ${r.yeniKod}. Eski kod takma ad olarak kaldı.`);
      setKodAcik(false); setYeniKod(''); setKodNot('');
      await yenile();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kod değiştirilemedi'); }
  }

  const islem = useDosyaIslem(id ?? 0, yenile, bildir);
  const donus = `/stok${donusOku()}`;

  if (yukHata && !d) {
    return (
      <div className="ccd-stack">
        <div><Link className="ccd-btn ccd-btn-sm ccd-sp-hap" to={donus}><Ikon ad="geri" boy={16} /> Ürünler</Link></div>
        <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{yukHata}</span></div>
      </div>
    );
  }
  if (!f || !ilk || (!yeni && !d)) return <div className="ccd-inline-note">Yükleniyor…</div>;

  /* Önceki / sonraki: listeden gelindiyse listenin sırası, yoksa kod sırası. */
  const sira = siraOku();
  const yer = id == null ? -1 : sira.indexOf(id);
  const onceki = yer > 0 ? sira[yer - 1] : yer === -1 ? d?.komsu?.onceki ?? null : null;
  const sonraki = yer >= 0 && yer < sira.length - 1 ? sira[yer + 1] : yer === -1 ? d?.komsu?.sonraki ?? null : null;

  const toplam = d ? Math.round((d.official + (d.offrec ?? 0)) * 1000) / 1000 : 0;
  const stokDurum = !d || d.category === 'HIZMET' ? null : toplam <= 0 ? 'tukendi' : toplam < d.threshold ? 'kritik' : null;
  const birimAd = UNIT_LABEL[f.unit as Unit];
  const listeKurus = (() => { const v = sayiOku(f.listPriceTl); return v == null || Number.isNaN(v) ? null : Math.round(v * 100); })();
  const elleMaliyet = (() => { const v = sayiOku(f.costTl); return v == null || Number.isNaN(v) ? null : Math.round(v * 100); })();
  const maliyetKurus = elleMaliyet ?? d?.maliyet?.maliyetKurus ?? null;
  const maliyetKaynak = elleMaliyet != null ? 'elle girilen maliyet' : d?.maliyet?.maliyetKaynak ?? null;
  const kar = listeKurus != null && maliyetKurus != null ? listeKurus - maliyetKurus : null;
  const marj = kar != null && listeKurus ? Math.round((kar / listeKurus) * 1000) / 10 : null;
  const hareketler = d ? (tumHareket ? d.hareketler : d.hareketler.slice(0, 10)) : [];
  const tedarikciKodlari = d ? d.aliases.filter((a) => !a.supplierCode.startsWith('parasut:')) : [];
  const kullanilmayanOzellik = OZELLIKLER.filter(([, e]) => !f.specs.some((s) => ozellikAnahtari(s.ad) === ozellikAnahtari(e)));

  function hareketDokumu() {
    if (!d) return;
    const h = (v: string | null) => {
      let x = v ?? '';
      // Formül enjeksiyonu: metin = + - @ ile başlıyorsa Excel çalıştırmasın (sayılar hariç).
      if (/^[=+\-@\t\r]/.test(x) && !/^-?\d+(,\d+)?$/.test(x)) x = `'${x}`;
      return /[";\r\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x;
    };
    const satirlar = [['Tarih', 'Hane', 'Miktar', 'Neden', 'Not', 'Kim'], ...d.hareketler.map((m) => [
      new Date(m.at).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }), CHANNEL_LABEL[m.channel], String(m.qty).replace('.', ','),
      MOVE_REASON_LABEL[m.reason] ?? m.reason, m.note ?? '', m.by,
    ])];
    dosyaKaydet(new Blob([`﻿${satirlar.map((r) => r.map(h).join(';')).join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' }), `${d.code}-stok-hareketleri.csv`);
  }

  const alanHata = (k: keyof Form) => (hatalar[k] ? <div className="ccd-uk-alan-hata" role="alert">{hatalar[k]}</div> : null);
  const hataSinif = (k: keyof Form) => (hatalar[k] ? ' ccd-uk-hatali' : '');

  return (
    <div className="ccd-uk">
      {/* ── Kaydetme çubuğu ── */}
      {(kirli || yeni) && esikYazar && (
        <div className="ccd-uk-kaydet" role="region" aria-label="Kaydet" data-tur="urun-kaydet">
          <span className="ccd-uk-kaydet-metin">
            <Ikon ad="uyari" boy={16} /> {yeni ? 'Yeni ürün — henüz kaydedilmedi' : 'Kaydedilmemiş değişiklikler'}
          </span>
          {hata && <span className="ccd-uk-kaydet-hata" role="alert">{hata}</span>}
          <span className="ccd-uk-kaydet-dugmeler">
            <button type="button" className="ccd-btn ccd-btn-sm ccd-uk-kaydet-vazgec" disabled={kaydediliyor}
                    onClick={() => { if (yeni) guvenliGit(donus); else { setF(ilk); setHatalar({}); setHata(null); } }}>Vazgeç</button>
            <button type="button" className="ccd-btn ccd-btn-sm ccd-uk-kaydet-dugme" disabled={kaydediliyor || (yeni && !kirli)} onClick={() => void kaydet()}>
              {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </span>
        </div>
      )}

      {/* ── Üst şerit ── */}
      <div className="ccd-uk-ust">
        <div className="ccd-uk-ust-sol">
          <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap ccd-uk-geri" onClick={() => guvenliGit(donus)} aria-label="Ürün listesine dön" title="Ürün listesine dön">
            <Ikon ad="geri" boy={16} />
          </button>
          <nav className="ccd-uk-kirinti" aria-label="Konum">
            <Link to={donus}>Ürünler</Link><span aria-hidden="true">›</span><span>{yeni ? 'Yeni ürün' : d!.code}</span>
          </nav>
        </div>
        {!yeni && d && (
          <div className="ccd-uk-ust-sag">
            {(urunYazar || d.hareketler.length > 0 || can('stock.code_change')) && (
              <div className="ccd-uk-menu-kap">
                <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((x) => !x)}>
                  Daha fazla eylem <Ikon ad="acilir" boy={14} />
                </button>
                {menu && (
                  <div className="ccd-uk-menu" role="menu">
                    {urunYazar && <button type="button" role="menuitem" onClick={() => guvenliGit(`/stok/yeni?kopya=${d.id}`)}>Kopyasını oluştur</button>}
                    {urunYazar && (
                      <button type="button" role="menuitem" onClick={() => al('active', !f.active)}>
                        {f.active ? 'Pasife al' : 'Aktifleştir'}<span className="ccd-uk-menu-not">Kaydet ile işlenir</span>
                      </button>
                    )}
                    {can('stock.code_change') && (
                      <button type="button" role="menuitem" onClick={() => { setKodAcik(true); setYeniKod(d.code); document.getElementById('uk-bilgi')?.scrollIntoView({ behavior: 'smooth' }); }}>Kodu değiştir</button>
                    )}
                    <button type="button" role="menuitem" onClick={() => document.getElementById('uk-muhasebe')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                      Muhasebe programı bağını göster
                    </button>
                    <button type="button" role="menuitem" disabled={d.hareketler.length === 0} title={d.hareketler.length === 0 ? 'Henüz stok hareketi yok' : undefined}
                            onClick={hareketDokumu}>
                      Hareket dökümünü indir<span className="ccd-uk-menu-not">son {d.hareketler.length} hareket · CSV</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <span className="ccd-uk-oklar">
              <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={onceki == null} aria-label="Önceki ürün" title="Önceki ürün"
                      onClick={() => onceki != null && guvenliGit(`/stok/${onceki}`)}><Ikon ad="yukari" boy={16} /></button>
              <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={sonraki == null} aria-label="Sonraki ürün" title="Sonraki ürün"
                      onClick={() => sonraki != null && guvenliGit(`/stok/${sonraki}`)}><Ikon ad="asagi" boy={16} /></button>
            </span>
          </div>
        )}
      </div>

      {/* ── Başlık ── */}
      <div className="ccd-uk-baslik">
        <h1 className="ccd-uk-ad">{f.name.trim() || (yeni ? 'Yeni ürün' : d!.name)}</h1>
        <div className="ccd-uk-rozetler">
          <span className={`ccd-badge ${f.active ? 'ccd-badge-success' : ''}`}>{f.active ? 'Aktif' : 'Pasif'}</span>
          {!yeni && <span className="ccd-uk-kod ccd-mono">{d!.code}</span>}
          {stokDurum === 'tukendi' && <span className="ccd-badge ccd-badge-danger">tükendi</span>}
          {stokDurum === 'kritik' && <span className="ccd-badge ccd-badge-warning">kritik stok</span>}
        </div>
      </div>

      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {hata && !((kirli || yeni) && esikYazar) && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      <div className="ccd-uk-sutunlar">
        {/* ═══════════════ SOL SÜTUN ═══════════════ */}
        <div className="ccd-uk-sol">
          {/* 1 · Ürün bilgisi */}
          <section className="ccd-uk-kart" id="uk-bilgi">
            <div className="ccd-uk-alan">
              <label className="ccd-uk-etiket" htmlFor="uk-ad">Ad</label>
              <input id="uk-ad" className={`ccd-input${hataSinif('name')}`} value={f.name} disabled={!urunYazar} maxLength={200} autoFocus={yeni}
                     placeholder="Bumpy White Mat 7,5x30" onChange={(e) => al('name', e.target.value)} />
              {alanHata('name')}
            </div>
            <div className="ccd-uk-alan">
              <label className="ccd-uk-etiket" htmlFor="uk-kod">Kod</label>
              {yeni ? (
                <>
                  <input id="uk-kod" className={`ccd-input ccd-mono${hataSinif('code')}`} value={f.code} placeholder="ARTBW7530M"
                         onChange={(e) => al('code', e.target.value.toUpperCase())} />
                  {alanHata('code') ?? <div className="ccd-field-hint">Tekil ve sabittir. Başka üründe varsa sistem söyler; sonradan değişirse eski kod takma ad olur.</div>}
                </>
              ) : !kodAcik ? (
                <div className="ccd-uk-kilitli">
                  <input id="uk-kod" className="ccd-input ccd-mono" value={d!.code} readOnly aria-describedby="uk-kod-not" />
                  {can('stock.code_change') && (
                    <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => { setKodAcik(true); setYeniKod(d!.code); }}>Değiştir</button>
                  )}
                  <div className="ccd-field-hint" id="uk-kod-not">Kod sabittir. Değiştirilirse eski kod takma ada düşer, geçmiş kopmaz.</div>
                </div>
              ) : (
                <form className="ccd-uk-kod-form" onSubmit={kodDegistir}>
                  <input className="ccd-input ccd-mono" required minLength={2} value={yeniKod} aria-label="Yeni kod" onChange={(e) => setYeniKod(e.target.value.toUpperCase())} />
                  <input className="ccd-input" required minLength={3} value={kodNot} placeholder="Neden değişiyor? (zorunlu)" aria-label="Gerekçe" onChange={(e) => setKodNot(e.target.value)} />
                  <button className="ccd-btn ccd-btn-primary ccd-btn-sm">Kodu değiştir</button>
                  <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setKodAcik(false)}>Vazgeç</button>
                </form>
              )}
            </div>
            <div className="ccd-uk-alan">
              <label className="ccd-uk-etiket" htmlFor="uk-aciklama">Açıklama</label>
              {urunYazar
                ? <AciklamaDuzenleyici deger={f.description} degis={(v) => al('description', v)} hata={hatalar.description} />
                : f.description.trim() ? <AciklamaGoster metin={f.description} /> : <div className="ccd-meta">Henüz açıklama yazılmadı.</div>}
            </div>
          </section>

          {/* 2 · Medya */}
          <section className="ccd-uk-kart">
            <h2 className="ccd-uk-kart-baslik">Medya <Ipucu id="medya-sirasi" /></h2>
            {yeni || !d ? (
              <div className="ccd-uk-medya-bos"><Ikon ad="gorsel" boy={28} /><span>Görseller ve belgeler ürün kaydedildikten sonra eklenir.</span></div>
            ) : (
              <>
                <MedyaIzgara d={d} yazabilir={urunYazar} islem={islem} />
                <div className="ccd-uk-ayrac" />
                <h3 className="ccd-uk-kart-altbaslik">Teknik çizim ve belgeler</h3>
                <Belgeler d={d} yazabilir={urunYazar} islem={islem} />
              </>
            )}
          </section>

          {/* 3 · Fiyat */}
          <section className="ccd-uk-kart">
            <h2 className="ccd-uk-kart-baslik">Fiyat</h2>
            <div className="ccd-uk-izgara3">
              <div className="ccd-uk-alan">
                <label className="ccd-uk-etiket" htmlFor="uk-liste">Liste fiyatı (₺, KDV hariç) <Ipucu id="kdv-haric-dahil" /></label>
                <input id="uk-liste" className={`ccd-input${hataSinif('listPriceTl')}`} inputMode="decimal" value={f.listPriceTl} disabled={!urunYazar}
                       placeholder="girilmemiş" onChange={(e) => al('listPriceTl', e.target.value)} />
                {alanHata('listPriceTl')}
              </div>
              <div className="ccd-uk-alan">
                <label className="ccd-uk-etiket" htmlFor="uk-kdv">KDV %</label>
                <input id="uk-kdv" className={`ccd-input${hataSinif('vatPct')}`} inputMode="decimal" value={f.vatPct} disabled={!urunYazar} onChange={(e) => al('vatPct', e.target.value)} />
                {alanHata('vatPct')}
              </div>
              <div className="ccd-uk-alan" data-fihrist="iskonto-tavani">
                <label className="ccd-uk-etiket" htmlFor="uk-tavan">Satış iskontosu tavanı % <Ipucu id="iskonto-tavani" /></label>
                <input id="uk-tavan" className={`ccd-input${hataSinif('maxDiscountPct')}`} inputMode="decimal" value={f.maxDiscountPct} disabled={!urunYazar}
                       placeholder="boş = markadan türetilir" onChange={(e) => al('maxDiscountPct', e.target.value)} />
                {alanHata('maxDiscountPct')}
              </div>
            </div>
            {maliyetGorur && (
              <>
                <div className="ccd-uk-ayrac" />
                <div className="ccd-uk-izgara3">
                  <div className="ccd-uk-alan" data-fihrist="birim-maliyet">
                    <label className="ccd-uk-etiket" htmlFor="uk-maliyet">Birim maliyet (₺, elle) <Ipucu id="birim-maliyet" /></label>
                    <input id="uk-maliyet" className={`ccd-input${hataSinif('costTl')}`} inputMode="decimal" value={f.costTl} disabled={!maliyetYazar}
                           placeholder="boş = fatura / iskontodan" onChange={(e) => al('costTl', e.target.value)} />
                    {alanHata('costTl') ?? <div className="ccd-field-hint">Yazılırsa alış faturası ve iskontonun önüne geçer.</div>}
                  </div>
                  <div className="ccd-uk-alan" data-fihrist="alis-iskontosu">
                    <label className="ccd-uk-etiket" htmlFor="uk-alis">Alış iskontosu % <Ipucu id="alis-iskontosu" /></label>
                    <input id="uk-alis" className={`ccd-input${hataSinif('purchaseDiscountPct')}`} inputMode="decimal" value={f.purchaseDiscountPct} disabled={!maliyetYazar}
                           placeholder={d?.maliyet?.alisIskontoPct != null && d.purchaseDiscountPct == null ? `%${alanMetni(d.maliyet.alisIskontoPct)} (markadan)` : 'boş = markadan'}
                           onChange={(e) => al('purchaseDiscountPct', e.target.value)} />
                    {alanHata('purchaseDiscountPct') ?? <div className="ccd-field-hint">Bu ürünü hep bu oranla alıyorsanız yazın; teklifte kendiliğinden gelir.</div>}
                  </div>
                </div>
                <div className="ccd-uk-ozet">
                  <div className="ccd-uk-ozet-kutu" data-fihrist="birim-maliyet">
                    <span className="ccd-uk-ozet-ad">Hesaplanan birim maliyet <Ipucu id="birim-maliyet" /></span>
                    <span className="ccd-uk-ozet-deger">{maliyetKurus != null ? formatTl(maliyetKurus) : <span className="ccd-kpi-down">bilinmiyor</span>}</span>
                    <span className="ccd-uk-ozet-not">{maliyetKurus != null ? `kaynak: ${maliyetKaynak}` : 'elle maliyet, alış faturası ya da alış iskontosu yok'}</span>
                  </div>
                  <div className="ccd-uk-ozet-kutu" data-fihrist="kar-marj">
                    <span className="ccd-uk-ozet-ad">Kâr (liste fiyatından) <Ipucu id="kar-marj" /></span>
                    <span className="ccd-uk-ozet-deger">{kar != null ? formatTl(kar) : '—'}</span>
                    <span className="ccd-uk-ozet-not">{marj != null ? `marj %${alanMetni(marj)}` : 'liste fiyatı ve maliyet gerekir'}</span>
                  </div>
                  {!yeni && d && (
                    <div className="ccd-uk-ozet-kutu" data-fihrist="depo-stok-degeri">
                      <span className="ccd-uk-ozet-ad">Stok maliyeti <Ipucu id="depo-stok-degeri" /></span>
                      <span className="ccd-uk-ozet-deger">{d.maliyet?.maliyetKurus != null ? formatTl(Math.round(d.maliyet.maliyetKurus * Math.max(0, toplam))) : '—'}</span>
                      <span className="ccd-uk-ozet-not">{miktar(Math.max(0, toplam))} {UNIT_LABEL[d.unit]} × birim maliyet</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          {/* 4 · Stok */}
          <section className="ccd-uk-kart">
            <div className="ccd-uk-kart-ust">
              <h2 className="ccd-uk-kart-baslik">Stok</h2>
              {!yeni && d && stokYazar && (
                <span className="ccd-uk-menu-kap">
                  <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" onClick={() => setStokKutu((x) => !x)} data-tur="stok-duzelt">Stok düzelt</button>
                  {stokKutu && (
                    <StokDuzelt urun={d} kanal="OFFICIAL" faturasizYazabilir={can('informal.write')} kapat={() => setStokKutu(false)}
                                bitti={(r) => { setStokKutu(false); setBilgi(`${CHANNEL_LABEL[r.kanal]}: ${miktar(r.oncesi)} → ${miktar(r.sonrasi)}`); void yenile(); }} />
                  )}
                </span>
              )}
            </div>
            {!yeni && d && (
              <div className="ccd-uk-rakamlar">
                <div className="ccd-uk-rakam" data-fihrist="resmi-stok">
                  <span className="ccd-uk-rakam-ad">Resmî <Ipucu id="resmi-stok" /></span>
                  <span className="ccd-uk-rakam-deger">{miktar(d.official)}</span>
                </div>
                {d.offrec != null && (
                  <div className="ccd-uk-rakam" data-fihrist="faturasiz-stok">
                    <span className="ccd-uk-rakam-ad">Faturasız <Ipucu id="faturasiz-stok" /></span>
                    <span className="ccd-uk-rakam-deger">{miktar(d.offrec)}</span>
                  </div>
                )}
                {d.fasonda != null && (
                  <div className="ccd-uk-rakam" data-fihrist="fasondaki-stok">
                    <span className="ccd-uk-rakam-ad">Fasonda <Ipucu id="fasondaki-stok" /></span>
                    <span className="ccd-uk-rakam-deger">{miktar(d.fasonda.toplam)}</span>
                    {d.fasonda.isler.length > 0 && (
                      <span className="ccd-uk-rakam-not">{d.fasonda.isler.map((i) => `${i.no} · ${i.fasoncu}`).slice(0, 2).join(', ')}{d.fasonda.isler.length > 2 ? '…' : ''}</span>
                    )}
                  </div>
                )}
                <div className="ccd-uk-rakam ccd-uk-rakam-ana" data-fihrist="toplam-stok">
                  <span className="ccd-uk-rakam-ad">Toplam <Ipucu id="toplam-stok" /></span>
                  <span className="ccd-uk-rakam-deger">{miktar(toplam)}</span>
                  <span className="ccd-uk-rakam-not">{UNIT_LABEL[d.unit]}{stokDurum ? ' · eşiğin altında' : ''}</span>
                </div>
              </div>
            )}
            <div className="ccd-uk-izgara3">
              <div className="ccd-uk-alan" data-fihrist="esik">
                <label className="ccd-uk-etiket" htmlFor="uk-esik">Uyarı eşiği <Ipucu id="esik" /></label>
                <input id="uk-esik" className={`ccd-input${hataSinif('threshold')}`} inputMode="decimal" value={f.threshold} disabled={!esikYazar} onChange={(e) => al('threshold', e.target.value)} />
                {alanHata('threshold') ?? <div className="ccd-field-hint">Toplam bu sayının altına düşünce uyarı üretilir.</div>}
              </div>
              <div className="ccd-uk-alan">
                <label className="ccd-uk-etiket" htmlFor="uk-birim">Stok birimi</label>
                <select id="uk-birim" className="ccd-select" value={f.unit} disabled={!urunYazar} onChange={(e) => al('unit', e.target.value)}>
                  {BIRIMLER.map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
                </select>
              </div>
              <div className="ccd-uk-alan" data-fihrist="kutu-m2">
                <label className="ccd-uk-etiket" htmlFor="uk-m2">1 kutu kaç m² <Ipucu id="kutu-m2" /></label>
                <input id="uk-m2" className={`ccd-input${hataSinif('boxM2')}`} inputMode="decimal" value={f.boxM2} disabled={!urunYazar} placeholder="örn. 1,44"
                       onChange={(e) => al('boxM2', e.target.value)} />
                {alanHata('boxM2') ?? <div className="ccd-field-hint">m² kesilen fatura kutu stoğundan bu oranla düşer.</div>}
              </div>
            </div>
            {yeni && <div className="ccd-meta">Açılış stoğu ürün kaydedildikten sonra "Stok düzelt" ile girilir (neden yazılarak).</div>}
            {!yeni && d && (
              <>
                <div className="ccd-uk-ayrac" />
                <div className="ccd-uk-kart-ust">
                  <h3 className="ccd-uk-kart-altbaslik">Son hareketler</h3>
                  {d.hareketler.length > 10 && (
                    <button type="button" className="ccd-uk-metin-dugme" onClick={() => setTumHareket((x) => !x)}>
                      {tumHareket ? 'Son 10 hareketi göster' : `Hareketleri gör (son ${d.hareketler.length})`}
                    </button>
                  )}
                </div>
                {d.hareketler.length === 0 ? <div className="ccd-meta">Henüz stok hareketi yok.</div> : (
                  <div className="ccd-uk-tablo-kap">
                    <table className="ccd-uk-tablo">
                      <thead>
                        <tr>
                          <th>Tarih</th><th>Hane</th><th className="ccd-uk-sayi">Miktar</th>
                          <th>Neden <Ipucu id="stok-hareketi" /></th><th>Not</th><th>Kim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hareketler.map((m) => (
                          <tr key={m.id}>
                            <td className="ccd-uk-tek">{formatTarih(m.at)}</td>
                            <td>{CHANNEL_LABEL[m.channel]}</td>
                            <td className="ccd-uk-sayi"><span className={m.qty < 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'}>{m.qty > 0 ? '+' : ''}{miktar(m.qty)}</span></td>
                            <td>{MOVE_REASON_LABEL[m.reason] ?? m.reason}</td>
                            <td>{m.note ?? <span className="ccd-muted">—</span>}</td>
                            <td className="ccd-muted">{m.by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>

          {/* 5 · Teknik özellikler */}
          <section className="ccd-uk-kart">
            <h2 className="ccd-uk-kart-baslik">Teknik özellikler</h2>
            {f.specs.length === 0 && <div className="ccd-meta">{urunYazar ? 'Henüz teknik özellik yok. Aşağıdan ekleyin: ebat, kalınlık, yüzey, kaymazlık sınıfı…' : 'Henüz teknik özellik girilmedi.'}</div>}
            {f.specs.map((s, n) => (
              <div className="ccd-uk-ozellik" key={n}>
                <input className="ccd-input" aria-label="Özellik adı" placeholder="Özellik adı" maxLength={60} list="uk-ozellik-adlari" value={s.ad} disabled={!urunYazar}
                       onChange={(e) => al('specs', f.specs.map((x, i) => (i === n ? { ...x, ad: e.target.value } : x)))} />
                <input className="ccd-input" aria-label={`${s.ad || 'Özellik'} değeri`} placeholder="Değer" maxLength={200} value={s.deger} disabled={!urunYazar}
                       onChange={(e) => al('specs', f.specs.map((x, i) => (i === n ? { ...x, deger: e.target.value } : x)))} />
                {urunYazar && (
                  <button type="button" className="ccd-uk-ikon-dugme" aria-label={`${s.ad || 'Özellik'} satırını sil`} title="Satırı sil"
                          onClick={() => al('specs', f.specs.filter((_, i) => i !== n))}><Ikon ad="cop" boy={16} /></button>
                )}
              </div>
            ))}
            <datalist id="uk-ozellik-adlari">{kullanilmayanOzellik.map(([k, e]) => <option key={k} value={e} />)}</datalist>
            {alanHata('specs')}
            {urunYazar && (
              <div className="ccd-uk-cipler">
                <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={f.specs.length >= 40}
                        onClick={() => al('specs', [...f.specs, { ad: '', deger: '' }])}><Ikon ad="arti" boy={14} /> Özellik ekle</button>
                {kullanilmayanOzellik.slice(0, 6).map(([k, e]) => (
                  <button type="button" key={k} className="ccd-uk-cip" disabled={f.specs.length >= 40} onClick={() => al('specs', [...f.specs, { ad: e, deger: '' }])}>+ {e.replace(/ \(.*\)$/, '')}</button>
                ))}
              </div>
            )}
          </section>

          {/* 6 · Alış ve satış geçmişi */}
          {!yeni && d && (
            <section className="ccd-uk-kart">
              <h2 className="ccd-uk-kart-baslik">Alış ve satış geçmişi</h2>

              {d.priceHistory && (
                <>
                  <h3 className="ccd-uk-kart-altbaslik">Alış fiyatları</h3>
                  {d.priceHistory.length === 0 ? <div className="ccd-meta">Henüz alış faturası bağlanmadı. Bir alış satırı bu ürüne bağlanınca buraya düşer.</div> : (
                    <div className="ccd-uk-tablo-kap">
                      <table className="ccd-uk-tablo">
                        <thead><tr><th>Tarih</th><th>Tedarikçi</th><th>Fatura</th><th className="ccd-uk-sayi">Birim fiyat <Ipucu id="son-alis" /></th></tr></thead>
                        <tbody>
                          {d.priceHistory.map((h) => (
                            <tr key={h.id}>
                              <td className="ccd-uk-tek">{formatTarih(h.date)}</td><td>{h.supplier ?? '—'}</td>
                              <td className="ccd-mono">{h.invoiceNo ?? '—'}</td><td className="ccd-uk-sayi">{formatTl(h.unitPriceKurus)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {gecmis?.satis && (
                <>
                  <h3 className="ccd-uk-kart-altbaslik">Satış faturaları</h3>
                  {!gecmis.satis.bagli ? <div className="ccd-meta">Ürün muhasebe programına bağlı olmadığı için satış faturaları bu ürüne eşlenemiyor.</div>
                    : gecmis.satis.faturalar.length === 0 ? <div className="ccd-meta">Bu ürünün geçtiği satış faturası yok.</div> : (
                      <div className="ccd-uk-tablo-kap">
                        <table className="ccd-uk-tablo">
                          <thead><tr><th>Tarih</th><th>Fatura</th><th>Cari</th><th className="ccd-uk-sayi">Miktar</th><th className="ccd-uk-sayi">Tutar (KDV hariç)</th></tr></thead>
                          <tbody>
                            {gecmis.satis.faturalar.map((x) => (
                              <tr key={x.id}>
                                <td className="ccd-uk-tek">{formatTarih(x.tarih)}</td>
                                <td className="ccd-mono">{x.no || '—'}{x.iade && <span className="ccd-badge ccd-badge-warning" style={{ marginLeft: 6 }}>iade</span>}</td>
                                <td>{x.cari}</td><td className="ccd-uk-sayi">{miktar(x.miktar)}</td><td className="ccd-uk-sayi">{formatTl(x.tutarKurus)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                </>
              )}

              {gecmis?.teklifler && (
                <>
                  <h3 className="ccd-uk-kart-altbaslik">Teklifler ve siparişler</h3>
                  {gecmis.teklifler.length === 0 ? <div className="ccd-meta">Bu ürün henüz bir teklifte geçmedi.</div> : (
                    <div className="ccd-uk-tablo-kap">
                      <table className="ccd-uk-tablo">
                        <thead><tr><th>Tarih</th><th>Teklif</th><th>Cari</th><th className="ccd-uk-sayi">Miktar</th><th className="ccd-uk-sayi">İskonto</th><th>Durum</th><th>Sipariş</th></tr></thead>
                        <tbody>
                          {gecmis.teklifler.map((t, n) => (
                            <tr key={`${t.id}-${n}`}>
                              <td className="ccd-uk-tek">{formatTarih(t.tarih)}</td>
                              <td><Link className="ccd-mono" to={`/teklif/${t.id}`}>{t.no}</Link></td>
                              <td>{t.cari ?? '—'}</td><td className="ccd-uk-sayi">{miktar(t.miktar)}</td><td className="ccd-uk-sayi">%{alanMetni(t.iskontoPct)}</td>
                              <td>{TEKLIF_DURUM[t.durum] ?? t.durum.toLocaleLowerCase('tr')}</td>
                              <td>{t.siparisler.length === 0 ? <span className="ccd-muted">—</span>
                                : t.siparisler.map((s) => <Link key={s.id} className="ccd-mono" to={`/siparis/${s.id}`} style={{ marginRight: 6 }}>{s.no}</Link>)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              <h3 className="ccd-uk-kart-altbaslik">Tedarikçi kodları ve eski kodlar <Ipucu id="esleme-kuyrugu" /></h3>
              {tedarikciKodlari.length === 0 ? <div className="ccd-meta">Henüz takma ad yok. İlk alış faturası bağlandığında ya da kod değiştiğinde buraya düşer.</div> : (
                <ul className="ccd-uk-takma">
                  {tedarikciKodlari.map((a) => (
                    <li key={a.id}>
                      <span className="ccd-mono">{a.supplierCode}</span>
                      <span className="ccd-meta">{a.supplier ?? 'tedarikçi belirsiz'}{a.nameSnapshot ? ` · "${a.nameSnapshot}"` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        {/* ═══════════════ SAĞ SÜTUN ═══════════════ */}
        <aside className="ccd-uk-sag">
          <section className="ccd-uk-yan-bolum">
            <h3 className="ccd-uk-yan-baslik"><label htmlFor="uk-durum">Durum</label></h3>
            <select id="uk-durum" className="ccd-select" value={f.active ? '1' : '0'} disabled={!urunYazar} onChange={(e) => al('active', e.target.value === '1')}>
              <option value="1">Aktif</option><option value="0">Pasif</option>
            </select>
            <p className="ccd-uk-yan-metin">{f.active ? 'Listelerde ve teklif aramasında görünür.' : 'Listelerde ve teklif aramasında görünmez; geçmişi silinmez.'}</p>
          </section>

          {!yeni && gecmis?.satis && (
            <section className="ccd-uk-yan-bolum">
              <h3 className="ccd-uk-yan-baslik">Satışlar</h3>
              {!gecmis.satis.bagli ? <p className="ccd-uk-yan-metin">Muhasebe programına bağlanınca satışlar burada görünür.</p>
                : gecmis.satis.gun90.miktar === 0 && gecmis.satis.gun90.tutarKurus === 0 ? <p className="ccd-uk-yan-metin">Bu ürün için yakın tarihli satış yok.</p> : (
                  <dl className="ccd-uk-satis">
                    <div><dt>Son 30 gün</dt><dd>{miktar(gecmis.satis.gun30.miktar)} {UNIT_LABEL[d!.unit]} · {formatTl(gecmis.satis.gun30.tutarKurus)}</dd></div>
                    <div><dt>Son 90 gün</dt><dd>{miktar(gecmis.satis.gun90.miktar)} {UNIT_LABEL[d!.unit]} · {formatTl(gecmis.satis.gun90.tutarKurus)}</dd></div>
                  </dl>
                )}
              <Link className="ccd-uk-yan-bag" to="/rapor/urunler">Ayrıntıları görüntüle</Link>
            </section>
          )}

          <section className="ccd-uk-yan-bolum">
            <h3 className="ccd-uk-yan-baslik">Ürün organizasyonu</h3>
            <div className="ccd-uk-alan">
              <label className="ccd-uk-etiket" htmlFor="uk-kategori">Kategori</label>
              <select id="uk-kategori" className="ccd-select" value={f.category} disabled={!urunYazar} onChange={(e) => al('category', e.target.value)}>
                {KATEGORILER.map((k) => <option key={k} value={k}>{KATEGORI_AD[k]}{k === 'HIZMET' ? ' (stok tutulmaz)' : ''}</option>)}
              </select>
            </div>
            <div className="ccd-uk-alan">
              <label className="ccd-uk-etiket" htmlFor="uk-marka">Marka</label>
              <input id="uk-marka" className={`ccd-input${hataSinif('brand')}`} list="uk-markalar" maxLength={80} value={f.brand} disabled={!urunYazar}
                     placeholder="yazdıkça önerilir" autoComplete="off" onChange={(e) => al('brand', e.target.value)} />
              <datalist id="uk-markalar">{markalar.map((m) => <option key={m} value={m} />)}</datalist>
              {alanHata('brand')}
            </div>
            <div className="ccd-uk-alan">
              <span className="ccd-uk-etiket">Birim</span>
              <span className="ccd-uk-yan-deger">{birimAd}{sayiOku(f.boxM2) ? ` · 1 kutu = ${f.boxM2} m²` : ''}</span>
            </div>
          </section>

          <section className="ccd-uk-yan-bolum">
            <h3 className="ccd-uk-yan-baslik"><label htmlFor="uk-site">Site</label></h3>
            <input id="uk-site" className={`ccd-input${hataSinif('siteName')}`} maxLength={200} value={f.siteName} disabled={!urunYazar}
                   placeholder="boş = ürün adıyla aynı" onChange={(e) => al('siteName', e.target.value)} />
            {alanHata('siteName') ?? <p className="ccd-uk-yan-metin">Sitede görünen ad; bayilerin kullandığı addan farklı olabilir.</p>}
          </section>

          {!yeni && d && <MuhasebeBagi d={d} yenile={yenile} bildir={bildir} />}

          <section className="ccd-uk-yan-bolum">
            <h3 className="ccd-uk-yan-baslik"><label htmlFor="uk-not">Not · seri / ebat / yüzey</label></h3>
            <textarea id="uk-not" className={`ccd-textarea${hataSinif('notes')}`} rows={3} maxLength={500} value={f.notes} disabled={!esikYazar}
                      placeholder="Bumpy Serisi · 7,5x30 · Mat" onChange={(e) => al('notes', e.target.value)} />
            {alanHata('notes') ?? <p className="ccd-uk-yan-metin">Listede adın altında görünür; arama bu alanı da tarar.</p>}
          </section>
        </aside>
      </div>
    </div>
  );
}
